'use client';

import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import Btn from '@components/Btn';
import { BASE_URL } from '@lib/constants';
import AuthenticationService from '@lib/auth/AuthenticationService';

const VOICE_TO_TEXT_ENDPOINT = `${BASE_URL}/wp-json/advisor/v1/voice-to-text`;

/**
 * Check whether the browser supports voice recording.
 *
 * This function only controls whether the Dictate button is shown.
 * It must never prevent the NotesModal component from rendering.
 *
 * @returns {boolean}
 */
function canDeviceUseDictation() {
	if (
		typeof window === 'undefined' ||
		typeof navigator === 'undefined'
	) {
		return false;
	}

	if (!navigator.mediaDevices?.getUserMedia) {
		return false;
	}

	if (typeof window.MediaRecorder === 'undefined') {
		return false;
	}

	return true;
}

/**
 * Get a readable error message.
 *
 * @param {unknown} error Error value.
 *
 * @returns {string}
 */
function getErrorMessage(error) {
	if (error instanceof Error && error.message) {
		return error.message;
	}

	return String(error || 'Unknown error');
}

/**
 * Decode HTML entities.
 *
 * @param {string} value Encoded value.
 *
 * @returns {string}
 */
function decodeHtmlEntities(value = '') {
	const textarea = document.createElement('textarea');

	textarea.innerHTML = value;

	return textarea.value;
}

/**
 * Get the best supported recording MIME type.
 *
 * @returns {string}
 */
function getSupportedRecordingMimeType() {
	if (
		typeof MediaRecorder === 'undefined' ||
		typeof MediaRecorder.isTypeSupported !== 'function'
	) {
		return '';
	}

	const mimeTypes = [
		'audio/webm;codecs=opus',
		'audio/webm',
		'audio/mp4',
		'audio/ogg;codecs=opus',
	];

	return (
		mimeTypes.find((mimeType) =>
			MediaRecorder.isTypeSupported(mimeType)
		) || ''
	);
}

/**
 * Get a file extension from an audio MIME type.
 *
 * @param {string} mimeType Audio MIME type.
 *
 * @returns {string}
 */
function getAudioFileExtension(mimeType = '') {
	if (mimeType.includes('webm')) {
		return 'webm';
	}

	if (mimeType.includes('ogg')) {
		return 'ogg';
	}

	if (mimeType.includes('mp4')) {
		return 'mp4';
	}

	if (mimeType.includes('mpeg')) {
		return 'mp3';
	}

	return 'webm';
}

export default function NotesModal({
	dialogRef = null,
	notes = '',
	modalClose = () => {},
	modalSave = () => {},
}) {
	const dispatch = useDispatch();

	const textareaRef = useRef(null);
	const mediaRecorderRef = useRef(null);
	const audioChunksRef = useRef([]);
	const streamRef = useRef(null);
	const isMountedRef = useRef(true);

	const [userNotes, setUserNotes] = useState('');
	const [dictationStatus, setDictationStatus] = useState('idle');
	const [dictationError, setDictationError] = useState('');
	const [canUseDictation, setCanUseDictation] = useState(false);

	const isRecording = dictationStatus === 'recording';
	const isTranscribing = dictationStatus === 'transcribing';
	const isBusy = isTranscribing;

	/**
	 * Get the current JWT authentication token.
	 *
	 * Attempts to refresh the token when one is not currently available.
	 *
	 * @returns {Promise<string>}
	 */
	const getAuthToken = async () => {
		const auth = new AuthenticationService();

		let token = auth.getJwtTokenFromStorage();

		if (!token) {
			await auth.refreshToken()(dispatch);
			token = auth.getJwtTokenFromStorage();
		}

		return token || '';
	};

	/**
	 * Stop the active microphone stream.
	 *
	 * @returns {void}
	 */
	const stopMicrophoneStream = () => {
		if (!streamRef.current) {
			return;
		}

		streamRef.current
			.getTracks()
			.forEach((track) => track.stop());

		streamRef.current = null;
	};

	/**
	 * Clean up the current recording.
	 *
	 * @returns {void}
	 */
	const cleanUpRecording = () => {
		stopMicrophoneStream();

		mediaRecorderRef.current = null;
		audioChunksRef.current = [];
	};

	useEffect(() => {
		setUserNotes(decodeHtmlEntities(notes || ''));
	}, [notes]);

	/*
	 * This only decides whether the Dictate button is shown.
	 * The dialog itself is never conditional.
	 */
	useEffect(() => {
		setCanUseDictation(canDeviceUseDictation());
	}, []);

	useEffect(() => {
		isMountedRef.current = true;

		return () => {
			isMountedRef.current = false;

			if (
				mediaRecorderRef.current &&
				mediaRecorderRef.current.state !== 'inactive'
			) {
				mediaRecorderRef.current.onstop = null;
				mediaRecorderRef.current.stop();
			}

			cleanUpRecording();
		};
	}, []);

	/**
	 * Save the current notes.
	 *
	 * @returns {void}
	 */
	const save = () => {
		modalSave(userNotes);
		setUserNotes('');
	};

	/**
	 * Update the notes value.
	 *
	 * @param {Object} event Change event.
	 *
	 * @returns {void}
	 */
	const notesChange = (event) => {
		setUserNotes(event.target.value);
	};

	/**
	 * Append a transcript to the existing notes.
	 *
	 * @param {string} transcript Transcribed voice recording.
	 *
	 * @returns {void}
	 */
	const appendTranscript = (transcript) => {
		if (!transcript?.trim()) {
			return;
		}

		setUserNotes((currentNotes) => {
			const cleanCurrentNotes = currentNotes || '';
			const cleanTranscript = transcript.trim();
			const separator =
				cleanCurrentNotes &&
				!cleanCurrentNotes.endsWith(' ')
					? ' '
					: '';

			return `${cleanCurrentNotes}${separator}${cleanTranscript}`;
		});

		window.setTimeout(() => {
			textareaRef.current?.focus();
		}, 0);
	};

	/**
	 * Send an audio recording to the voice-to-text endpoint.
	 *
	 * @param {Blob} audioBlob Recorded audio.
	 *
	 * @returns {Promise<string>}
	 */
	const transcribeAudio = async (audioBlob) => {
		const token = await getAuthToken();

		if (!token) {
			throw new Error( 'Your session has expired. Please log in again.' );
		}

		const extension = getAudioFileExtension(audioBlob.type);
		const formData = new FormData();

		formData.append(
			'voice',
			audioBlob,
			`recording.${extension}`
		);

		formData.append('language', 'en');

		const response = await fetch(
			VOICE_TO_TEXT_ENDPOINT,
			{
				method: 'POST',
				headers: {
					Authorization: `Bearer ${token}`,
				},
				body: formData,
			}
		);

		let data;

		try {
			data = await response.json();
		} catch {
			throw new Error(
				'The transcription service returned an invalid response.'
			);
		}

		if (!response.ok) {
			throw new Error( data?.message || 'The voice recording could not be transcribed.' );
		}

		if (!data?.text || typeof data.text !== 'string') {
			throw new Error( 'The transcription service returned no text.' );
		}

		return data.text.trim();
	};

	/**
	 * Stop the active recording.
	 *
	 * @returns {void}
	 */
	const stopRecording = () => {
		if ( mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive' ) {
			mediaRecorderRef.current.stop();
		}
	};

	/**
	 * Start recording audio from the user's microphone.
	 *
	 * @returns {Promise<void>}
	 */
	const startRecording = async () => {
		if (!canDeviceUseDictation()) {
			setCanUseDictation(false);

			return;
		}

		setDictationError('');

		let stream;

		try {
			stream = await navigator.mediaDevices.getUserMedia({
				audio: {
					echoCancellation: true,
					noiseSuppression: true,
					autoGainControl: true,
					channelCount: 1,
				},
			});
		} catch (error) {
			console.error('Microphone error:', error);

			if ( error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError' ) {
				setDictationError( 'Microphone permission was denied. Please allow microphone access in your browser settings.' );
			} else if ( error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError' ) {
				setDictationError( 'No microphone was found on this device.' );
			} else if (error.name === 'NotReadableError') {
				setDictationError( 'Your microphone is already in use by another app or browser tab.' );
			} else if (error.name === 'SecurityError') {
				setDictationError( 'Microphone access is unavailable.' );
			} else {
				setDictationError( `Microphone could not start: ${getErrorMessage(error)}` );
			}

			return;
		}

		if (!isMountedRef.current) {
			stream.getTracks().forEach((track) => track.stop());

			return;
		}

		try {
			streamRef.current = stream;
			audioChunksRef.current = [];

			const mimeType = getSupportedRecordingMimeType();
			const mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);

			mediaRecorder.ondataavailable = (event) => {
				if (event.data.size > 0) {
					audioChunksRef.current.push(event.data);
				}
			};

			mediaRecorder.onerror = (event) => {
				console.error(
					'MediaRecorder error:',
					event.error
				);

				if (isMountedRef.current) {
					setDictationError( `Recording failed: ${getErrorMessage( event.error )}`);

					setDictationStatus('idle');
				}

				cleanUpRecording();
			};

			mediaRecorder.onstop = async () => {
				const recordedChunks = [
					...audioChunksRef.current,
				];

				audioChunksRef.current = [];
				stopMicrophoneStream();

				if (!recordedChunks.length) {
					if (isMountedRef.current) {
						setDictationError(
							'No audio was captured. Please try again.'
						);

						setDictationStatus('idle');
					}

					cleanUpRecording();

					return;
				}

				try {
					if (isMountedRef.current) {
						setDictationStatus('transcribing');
					}

					const recordedMimeType = mediaRecorder.mimeType || mimeType || 'audio/webm';

					const audioBlob = new Blob(
						recordedChunks,
						{
							type: recordedMimeType,
						}
					);

					recordedChunks.length = 0;

					const transcript = await transcribeAudio(audioBlob);

					if (isMountedRef.current) {
						appendTranscript(transcript);
					}
				} catch (error) {
					console.error(
						'Transcription error:',
						error
					);

					if (isMountedRef.current) {
						setDictationError( `Could not transcribe the recording: ${getErrorMessage( error )}`);
					}
				} finally {
					cleanUpRecording();

					if (isMountedRef.current) {
						setDictationStatus('idle');
					}
				}
			};

			mediaRecorderRef.current = mediaRecorder;

			/*
			 * Recording continues until the Stop button is clicked.
			 */
			mediaRecorder.start();

			setDictationStatus('recording');
		} catch (error) {
			console.error('Recorder error:', error);

			stream.getTracks().forEach((track) => track.stop());

			cleanUpRecording();

			if (isMountedRef.current) {
				setDictationStatus('idle');

				setDictationError( `Recording could not start: ${getErrorMessage( error )}`);
			}
		}
	};

	/**
	 * Start or stop voice recording.
	 *
	 * @returns {void}
	 */
	const toggleDictation = () => {
		if (isBusy) {
			return;
		}

		if (isRecording) {
			stopRecording();

			return;
		}

		void startRecording();
	};

	/**
	 * Get the dictate button text.
	 *
	 * @returns {string}
	 */
	const getDictationButtonText = () => {
		if (isRecording) {
			return 'Stop';
		}

		if (isTranscribing) {
			return 'Transcribing...';
		}

		return 'Dictate';
	};

	return (
		<dialog
			ref={dialogRef}
			id="notesDialog"
			className="notesModal"
		>
			<div className="notesModalInner">
				<div>
					<div className="notesModalHeader">
						<h2>Profile Notes</h2>

						{canUseDictation && (
							<button
								type="button"
								className={`dictationButton ${ isRecording ? 'recording' : '' }`}
								onClick={toggleDictation}
								disabled={isBusy}
								aria-label={ isRecording ? 'Stop dictation' : 'Start dictation' }
								title={ isRecording ? 'Stop dictation' : 'Start dictation' }
							>
								<span className="dictationText">
									{getDictationButtonText()}
								</span>
							</button>
						)}
					</div>

					<textarea
						autoFocus
						value={userNotes}
						ref={textareaRef}
						id="modal-notes"
						onChange={notesChange}
					/>

					{canUseDictation &&
						dictationStatus !== 'idle' && (
							<p className="dictationStatus" aria-live="polite" >
								{isRecording && 'Recording your note. Click Stop when finished.'}

								{isTranscribing && 'Transcribing your note...'}
							</p>
						)}

					{canUseDictation &&
						dictationError && (
							<p className="dictationUnsupported" role="alert" >
								{dictationError}
							</p>
						)}
				</div>
			</div>

			<div className="notesModalFooter">
				<Btn
					customClass={[ 'outline', 'modal']}
					onClick={modalClose}
				>
					Cancel
				</Btn>

				<Btn
					customClass={[ 'primary', 'modal']}
					onClick={save}
				>
					Save
				</Btn>
			</div>
		</dialog>
	);
}